/**
 * CookieConsentContext.jsx
 *
 * Gestión central del consentimiento de cookies.
 *
 * Conforme con:
 *   • RGPD (UE 2016/679)
 *   • LOPDGDD 3/2018
 *   • Guía sobre el uso de cookies de la AEPD (Nov 2023)
 *
 * Categorías:
 *   • necessary   → siempre activas (sesión, idioma, seguridad, consentimiento)
 *   • analytics   → Google Analytics 4 (anónima, agregada)
 *   • marketing   → futuras integraciones de remarketing/Google Ads
 *
 * Almacenamiento: localStorage clave `revix_cookie_consent_v1`.
 *   Estructura: { necessary: true, analytics: bool, marketing: bool, ts: ISO }
 *
 * Google Consent Mode v2: por defecto TODO denied; al aceptar, se actualiza
 * via gtag('consent', 'update', {...}).
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'revix_cookie_consent_v1';
const VERSION = 1;

const CONSENT_DEFAULT = {
  necessary: true,   // siempre true (técnicamente imprescindibles)
  analytics: false,
  marketing: false,
  ts: null,
  version: VERSION,
};

const CookieConsentCtx = createContext(null);

// ─── Google Consent Mode v2 helpers ─────────────────────────────────────────
function gtag(...args) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

function applyConsentModeDefaults() {
  // Inicialización con TODO denegado (debe ejecutarse antes de cargar GA).
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtagFn() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtagFn;
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',  // necesarias
    security_storage: 'granted',        // seguridad
    wait_for_update: 500,
  });
}

function applyConsentMode(consent) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });
}

// ─── Carga dinámica de Google Analytics 4 ───────────────────────────────────
function loadGoogleAnalytics() {
  if (typeof window === 'undefined') return;
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId) return; // sin ID configurado, no se carga
  if (window.__ga4_loaded) return;
  window.__ga4_loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);

  window.gtag = window.gtag || function gtagFn() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
  });
}

// ─── Provider ───────────────────────────────────────────────────────────────
export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(CONSENT_DEFAULT);
  const [hasChosen, setHasChosen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Inicialización: aplicar defaults gtag y leer localStorage
  useEffect(() => {
    applyConsentModeDefaults();
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        if (stored && stored.version === VERSION) {
          setConsent({ ...CONSENT_DEFAULT, ...stored });
          setHasChosen(true);
          applyConsentMode(stored);
          if (stored.analytics) loadGoogleAnalytics();
          return;
        }
      }
    } catch (_) { /* corrupto → reset */ }
    setShowBanner(true);
  }, []);

  // Persistir y aplicar
  const saveConsent = useCallback((next) => {
    const payload = {
      ...CONSENT_DEFAULT,
      ...next,
      necessary: true,
      ts: new Date().toISOString(),
      version: VERSION,
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) {}
    setConsent(payload);
    setHasChosen(true);
    setShowBanner(false);
    setShowSettings(false);
    applyConsentMode(payload);
    if (payload.analytics) loadGoogleAnalytics();
    // Notificación opcional al backend (registro de consentimiento)
    try {
      const url = process.env.REACT_APP_BACKEND_URL;
      if (url) {
        fetch(`${url}/api/public/consent-log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'cookies',
            preferences: {
              analytics: payload.analytics,
              marketing: payload.marketing,
            },
            ts: payload.ts,
            ua: navigator.userAgent,
          }),
        }).catch(() => {});
      }
    } catch (_) {}
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  }, [saveConsent]);

  const updatePreferences = useCallback((prefs) => {
    saveConsent({ ...consent, ...prefs });
  }, [consent, saveConsent]);

  const openSettings = useCallback(() => setShowSettings(true), []);
  const closeSettings = useCallback(() => setShowSettings(false), []);
  const reopenBanner = useCallback(() => setShowBanner(true), []);

  const value = {
    consent,
    hasChosen,
    showBanner,
    showSettings,
    acceptAll,
    rejectAll,
    updatePreferences,
    openSettings,
    closeSettings,
    reopenBanner,
  };

  return <CookieConsentCtx.Provider value={value}>{children}</CookieConsentCtx.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentCtx);
  if (!ctx) throw new Error('useCookieConsent debe usarse dentro de CookieConsentProvider');
  return ctx;
}
